import { reactive } from "vue"
import axios from "axios"
import util from "@plugins/util"
/* eslint-disable no-unused-vars */
import { Dashboard, DataType } from "../models/dashboard.js"
/* eslint-enable no-unused-vars */
const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
/**
 * @typedef {Object} DashboardState
 */
export const dashboardState = reactive({
  isLoading: true,
  isBubblesLoading: true,

  /** @type {Dashboard[]} */
  dashboardList: null,
  loadDashboardList,

  /** @type {Dashboard} */
  dashboard: {
    id: -1,
    name: "",
    active: false,
    institutionId: -1,
    groupId: -1,
    layers: [],
    data: [],
  },
  /** @type {DataType[]} */
  dataTypeList: null,
  /**@type {DataType} */
  dataType: {
    id: -1,
    identifier: "",
    group: "",
    period: "",
    example: "",
    defaultIcon: {
      id: -1,
      name: "",
      data: "",
    },
    defaultBackground: {
      id: -1,
      name: "",
      data: "",
    },
    defaultTextIcon: "",
    defaultTextMain: "",
    defaultTextDetail: "",
    defaultTextColorIcon: "",
    defaultTextColorMain: "",
    defaultTextColorDetail: "",
  },
  loadDashboard,
  startUpdateData,
  startUpdateResource,
  loadBubbleTypes,
  loadPublicDashboard,
  loadDashboardById,
  startUpdateActiveDashboard,
  startUpdateDashboardForGroup,
  startUpdateDashboard
})

/**
 * Loads data (bubble) types
 */
export async function loadBubbleTypes() {
  try {
    if (!dashboardState.dataTypeList) {
      dashboardState.isBubblesLoading = true
      const response = await axios.get(
        util.middleware() + "/dashboard/data/type"
      )
      dashboardState.dataTypeList = response.data
      dashboardState.isBubblesLoading = false
    }
  } catch (error) {
    console.error("Error loadBubbleTypes:", error)
  }
}
/**
 * Deletes a bubble from the dashboard data.
 *
 * @param {Object} dataObj - The bubble object to delete.
 */
export function dashboardStateDeleteBubble(dataObj) {
  dashboardState.dashboard.data = dashboardState.dashboard.data.filter(
    (data) => data !== dataObj
  )
}
/**
 * Loads Dashboard list
 */
async function loadDashboardList() {
  try {
    dashboardState.isLoading = true
    const response = await axios.get(util.middleware() + "/dashboard/data")
    dashboardState.dashboardList = response.data
    dashboardState.isLoading = false
  } catch (error) {
    console.error("Error loadDashboardList:", error)
  }
}
/**
 * Saves Dashboard data from editor changes
 *
 */
export async function saveDashboardEditorChanges() {
  try {
    if(!isMobileDevice && (window.screen.width != 1920 || window.screen.height != 1080)){
      resetXYcoordinates(window.screen.width / 1920, window.screen.height / 1080)
    }
    const response = await axios.post(
      util.middleware() + "/dashboard/data",
      dashboardState.dashboard
    )

    return response.status
  } catch (error) {
    console.error("Error saveDashboardEditorChanges:", error)
  }
}

/**
 * Loads a specific dashboard by its ID.
 *
 * @async
 * @function loadDashboard
 * @param {string} id - The ID of the dashboard to load.
 * @param {boolean} isEditor - A flag indicating whether the dashboard is being loaded in editor mode.
 * @throws Will throw an error if the request fails.
 */
async function loadDashboard(id, isEditor) {
  try {
    dashboardState.isLoading = true
    const response = await axios.get(
      util.middleware() +
        "/dashboard/data/" +
        id +
        "?editor=" +
        (isEditor ? "true" : "false")
    )
    //const response = { data: debugData }
    dashboardState.dashboard == undefined
    dashboardState.dashboard = response.data
    if(!isMobileDevice && (window.screen.width != 1920 || window.screen.height != 1080)){
      resetXYcoordinates(1920 / window.screen.width , 1080 / window.screen.height)
    }
    dashboardState.isLoading = false
  } catch (error) {
    console.error("Error getInitDashboardData:", error)
  }
}
/**
 * Loads a specific dashboard by its ID.
 *
 * @async
 * @function loadDashboardById
 * @param {string} id - The ID of the dashboard to load.
 * @throws Will throw an error if the request fails.
 */
async function loadDashboardById(id) {
  try {
    dashboardState.isLoading = true
    const response = await axios.get(
      util.middleware() +
        "/public-dashboard/dashboard/" + id
    )
    //const response = { data: debugData }
    dashboardState.dashboard == undefined
    dashboardState.dashboard = response.data
    if(!isMobileDevice && (window.screen.width != 1920 || window.screen.height != 1080)){
      resetXYcoordinates(1920 / window.screen.width , 1080 / window.screen.height)
    }
    dashboardState.isLoading = false
  } catch (error) {
    console.error("Error getInitDashboardData:", error)
  }
}
/**
 * Resets the x and y coordinates of the dashboard elements if user screen size is not equal to 1920x1080.
 * @param {number} ratioX - The ratio to scale the x coordinates by.
 * @param {number} ratioY - The ratio to scale the y coordinates by.
 * @returns {void}
  */
function resetXYcoordinates(ratioX, ratioY) {
  dashboardState.dashboard.layers.forEach((layer) => {
   layer.elements.forEach((element) => {
      element.xPosition == Math.round(element.xPosition / ratioX)
      element.yPosition == Math.round(element.yPosition / ratioY)
    })
  })
}
/**
 * Loads a public dashboard based on the provided group ID.
 *
 * @async
 * @function loadPublicDashboard
 * @param {string} groupId - The ID of the group for which to load the public dashboard.
 * @throws Will throw an error if the request fails.
 */
async function loadPublicDashboard(groupId) {
  try {
    dashboardState.isLoading = true
    let response
    if (groupId) {
      response = await axios.post(
        util.middleware() + "/public-dashboard/active",
        { group: groupId }
      )
    } else {
      response = await axios.post(
        util.middleware() + "/public-dashboard/active",
        {}
      )
    }
    dashboardState.dashboard == undefined
    dashboardState.dashboard = response.data
    if(!isMobileDevice && (window.screen.width != 1920 || window.screen.height != 1080)){
      resetXYcoordinates(1920 / window.screen.width , 1080 / window.screen.height)
    }
    dashboardState.isLoading = false
  } catch (error) {
    console.error("Error loadPublicDashboard:", error)
  }
}

/**
 * Updates the data of the current dashboard.
 *
 * @async
 * @function updateDashboardData
 * @throws Will throw an error if the request fails.
 */
async function updateDashboardData() {
  try {
    const id = dashboardState.dashboard.id
    const response = await axios.get(
      util.middleware() + "/dashboard/" + id + "/data"
    )
    dashboardState.dashboard.data = []
    dashboardState.dashboard.data = response.data
  } catch (error) {
    console.error("Error update data:", error)
  }
}
/**
 * Starts updating the active dashboard at a specified interval.
 *
 * @param {number|string} updateInterval - The interval at which to update the active dashboard, in milliseconds. This can be a number or a string that can be evaluated to a number.
 * @returns {number} The ID of the interval timer. This can be used with `clearInterval` to stop updating the active dashboard.
 * @throws {Error} If an error occurs while starting the update.
 */
export function startUpdateActiveDashboard(updateInterval) {
  try {
    const intervalId = setInterval(() => {
        loadPublicDashboard()
    }, eval(updateInterval))

    return intervalId
  } catch (error) {
    console.error("Error startUpdateActiveDashboard:", error)
  }
}

/**
 * Starts updating the active dashboard at a specified interval.
 *
 * @param {number|string} updateInterval - The interval at which to update the active dashboard, in milliseconds. This can be a number or a string that can be evaluated to a number.
 * @param {number|string} groupId - The group Id
 * @returns {number} The ID of the interval timer. This can be used with `clearInterval` to stop updating the active dashboard.
 * @throws {Error} If an error occurs while starting the update.
 */
export function startUpdateDashboardForGroup(updateInterval, groupId) {
  try {
    const intervalId = setInterval(() => {
        loadPublicDashboard(groupId)
    }, eval(updateInterval))

    return intervalId
  } catch (error) {
    console.error("Error startUpdateActiveDashboard:", error)
  }
}

/**
 * Starts updating the active dashboard at a specified interval.
 *
 * @param {number|string} updateInterval - The interval at which to update the active dashboard, in milliseconds. This can be a number or a string that can be evaluated to a number.
 * @param {number|string} dashboardId - The dashboard Id
 * @returns {number} The ID of the interval timer. This can be used with `clearInterval` to stop updating the active dashboard.
 * @throws {Error} If an error occurs while starting the update.
 */
export function startUpdateDashboard(updateInterval, dashboardId) {
  try {
    const intervalId = setInterval(() => {
        loadDashboardById(dashboardId)
    }, eval(updateInterval))

    return intervalId
  } catch (error) {
    console.error("Error startUpdateDashboard:", error)
  }
}
/**
 * Starts the process of updating dashboard data at a specified interval.
 *
 * @function startUpdateData
 * @param {number} updateInterval - The interval (in milliseconds) at which to update the dashboard data.
 * @returns {number} The ID of the interval timer. This can be used to cancel the timer later.
 * @throws Will throw an error if the interval fails to start.
 */
export function startUpdateData(updateInterval) {
  try {
    const intervalId = setInterval(() => {
      updateDashboardData()
    }, eval(updateInterval))

    return intervalId
  } catch (error) {
    console.error("Error startUpdateData:", error)
  }
}

/**
 * Updates the resources of the current dashboard.
 *
 * @async
 * @function updateDashboardResource
 * @throws Will throw an error if the request fails.
 */
async function updateDashboardResource() {
  try {
    const id = dashboardState.dashboard.id
    const response = await axios.get(
      util.middleware() + "/dashboard/" + id + "/resources"
    )
    dashboardState.dashboard.layers = []
    dashboardState.dashboard.layers = response.data
    if(!isMobileDevice && (window.screen.width != 1920 || window.screen.height != 1080)){
      resetXYcoordinates(1920 / window.screen.width , 1080 / window.screen.height)
    }
  } catch (error) {
    console.error("Error update resource:", error)
  }
}
/**
 * Starts the process of updating dashboard resources at a specified interval.
 *
 * @function startUpdateResource
 * @param {number} updateInterval - The interval (in milliseconds) at which to update the dashboard resources.
 * @returns {number} The ID of the interval timer. This can be used to cancel the timer later.
 * @throws Will throw an error if the interval fails to start.
 */
export function startUpdateResource(updateInterval) {
  try {
    const intervalId = setInterval(() => {
      updateDashboardResource()
    }, eval(updateInterval))
    return intervalId
  } catch (error) {
    console.error("Error startUpdateResource:", error)
  }
}
